import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Grid } from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import { GeneralFieldsWrapper } from "../Forms/GeneralFieldsWrapper";
import { CrewPositionDropTarget } from "../CrewPositionDropTarget";
import { IconButton } from "src/components/IconButton/IconButton";
import { DisplayCrewOnPairing } from "./DisplayCrewOnPairing";
import EditRounded from "@mui/icons-material/EditRounded";
import ViewIcon from "@mui/icons-material/Visibility";
import { useTheme } from "@mui/material";
import { isEqual } from "lodash";
import type { ICalculatePlannedCreditResult, IPairing } from "@vmosolutions/oc-interfaces";
import type { IRootState } from "src/redux/reducers/CombinedReducers";
import type { IScenarioViolationEvaluation } from "../ScenarioPreCheck/FetchScenarioViolations";
import { fetchScenarioViolations } from "../ScenarioPreCheck/FetchScenarioViolations";
import { ScenarioViolationsPanel } from "../ScenarioPreCheck/ScenarioViolationsPanel";

type CrewPositionsTabProps = {
  id: string;
  windowId: string;
  path: string[];
  className: string;
  initPairing: IPairing;
  containerRef: React.MutableRefObject<HTMLDivElement>;
  iteration: number;
  /** True when the editor holds unsaved edits. */
  dirty: boolean;
  /** Applies the records a credit recalculation wrote. */
  onCreditRecalculated: (result: ICalculatePlannedCreditResult) => void | Promise<void>;
};

export const CrewPositionsTab = (props: CrewPositionsTabProps): JSX.Element => {
  const { id, initPairing, containerRef, iteration, dirty, onCreditRecalculated } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const [editCrew, setEditCrew] = useState<boolean>(true);
  const storedPairing = useSelector(
    (state: IRootState): IPairing | undefined =>
      state.crewSchedule.pairingEditors[id],
  );

  // Regulations pre-check ("scenario"): on demand, splice the current (unsaved) pairing into each assigned
  // crew's schedule on the backend and surface the NEW violations it would cause - before saving.
  const [scenarioResult, setScenarioResult] =
    useState<IScenarioViolationEvaluation | null>(null);
  const [scenarioLoading, setScenarioLoading] = useState<boolean>(false);
  // Bumped on each successful pre-check so the results panel can remount its rows and re-apply their
  // default-expanded state (otherwise a re-run with new violations could stay collapsed).
  const [scenarioRunId, setScenarioRunId] = useState<number>(0);
  const canPreCheck = (storedPairing?.eventGroups.length ?? 0) > 0;

  const runPreCheck = useCallback(async (): Promise<void> => {
    if (!storedPairing) {
      return;
    }
    setScenarioLoading(true);
    try {
      // `draft: true` - evaluate the live editor state, omitting the saved version of this pairing from the splice.
      const result = await fetchScenarioViolations(
        storedPairing,
        true,
        dispatch,
      );
      setScenarioResult(result);
      setScenarioRunId((prev) => prev + 1);
    } finally {
      setScenarioLoading(false);
    }
  }, [storedPairing, dispatch]);

  const isDirty = useMemo(
    () => !isEqual(initPairing.positions, storedPairing?.positions),
    [initPairing.positions, storedPairing?.positions],
  );
  const menuBtnStyle = useMemo(() => {
    return {
      color: isDirty
        ? theme.custom.forms.dirtyColor
        : theme.typography.body2.color,
      width: "14px",
      height: "14px",
      border: "1px solid",
      borderRadius: 3,
      margin: "0px 5px",
    };
  }, [isDirty]);

  return (
    <Grid container direction="column" className={props.className}>
      <Grid item style={{ width: "100%" }}>
        <GeneralFieldsWrapper id={id} pairingDirty={dirty} onCreditRecalculated={onCreditRecalculated} />
      </Grid>

      <Grid item className="webgl_pairing" style={{ position: "relative" }}>
        <Grid
          item
          style={{
            height: "100%",
            width: "100%",
            display: editCrew ? "flex" : "none",
          }}
        >
          <CrewPositionDropTarget {...props} />
        </Grid>
        <Grid
          item
          style={{
            height: "100%",
            width: "100%",
            display: !editCrew ? "flex" : "none",
          }}
        >
          <DisplayCrewOnPairing
            containerRef={containerRef}
            iteration={iteration}
            pairingPositions={storedPairing?.positions ?? []}
          />
        </Grid>

        {editCrew ? (
          <IconButton
            id="display_crew_slots_btn"
            style={{
              width: 24,
              height: 24,
              position: "absolute",
              top: 5,
              right: 5,
            }}
            tooltipProps={{ title: "Switch to Display Mode" }}
            onClick={() => setEditCrew(false)}
            size="large"
          >
            <ViewIcon style={menuBtnStyle} />
          </IconButton>
        ) : (
          <IconButton
            id="edit_crew_slots_btn"
            style={{
              width: 24,
              height: 24,
              position: "absolute",
              top: 5,
              right: 5,
            }}
            tooltipProps={{ title: "Switch to Edit Mode" }}
            onClick={() => setEditCrew(true)}
            size="large"
          >
            <EditRounded style={menuBtnStyle} />
          </IconButton>
        )}
      </Grid>

      <Grid item style={{ width: "100%" }}>
        <Button
          id="run_regs_precheck_btn"
          size="small"
          variant="outlined"
          startIcon={<RuleIcon />}
          disabled={!canPreCheck || scenarioLoading}
          onClick={() => void runPreCheck()}
          sx={{ m: 1, textTransform: "none", fontSize: 12 }}
        >
          Pre-check violations
        </Button>
        <ScenarioViolationsPanel
          result={scenarioResult}
          loading={scenarioLoading}
          storedPairing={storedPairing}
          runId={scenarioRunId}
        />
      </Grid>
    </Grid>
  );
};
